// @ts-nocheck
import * as React from "react";
import { Ic, Sidebar, Topbar, AppFooter } from "./common";
import { copyText } from "../components/clipboard";
import { requestWithCsrf } from "../components/serverUpload";

// 파일전송 — File Transfer.
// 시스템 관리자가 서버로 파일/이미지를 올리고, 그 절대경로를 복사해
// AI(클로드)에게 전달하기 위한 화면. 백엔드 Admin::UploadsController:
//   POST   /dev/upload/file            (멀티파트)
//   POST   /dev/upload/clipboard       (base64 · 붙여넣기)
//   POST   /dev/upload/from_attachment (보드 첨부 → uploads 사본, dev-board에서 사용)
//   GET    /dev/upload/list            (목록)
//   DELETE /dev/upload/file?filename=  (정리)
//   GET    /storage/uploads/:name      (인라인 미리보기)
// 전 엔드포인트 system_admin 세션 + CSRF 토큰 필요.

const IMG_RE = /\.(png|jpe?g|gif|webp|svg|bmp|avif)$/i;

function fmtSize(bytes) {
  if (bytes == null) return "";
  if (bytes === 0) return "0 B";
  const k = 1024, sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
}

function FileTransfer() {
  const [files, setFiles] = React.useState([]);   // 서버 파일 목록(최신순)
  const [dragOver, setDragOver] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const [copied, setCopied] = React.useState(null); // 방금 복사한 filepath(또는 "선택")
  const [q, setQ] = React.useState("");
  const [picked, setPicked] = React.useState(() => new Set()); // 다중 선택된 filepath
  const inputRef = React.useRef(null);

  const loadList = React.useCallback(() => {
    // 캐시 버스터 — 이미 설치된 구버전 서비스워커(동일출처 GET을 stale-while-revalidate로
    // 캐시하던 버전)가 옛 목록을 돌려주는 것을 막는다. SW 캐시 키는 URL 전체라 매번
    // 다른 URL이면 무조건 네트워크로 나간다. (SW 자체도 /assets/*만 캐시하도록 수정)
    fetch(`/dev/upload/list?t=${Date.now()}`, { credentials: "same-origin", cache: "no-store" })
      .then(r => r.json())
      .then(d => {
        if (d.success) setFiles(d.files || []);
        else setError(d.error || "목록을 불러오지 못했습니다.");
      })
      .catch(() => setError("목록을 불러오지 못했습니다."));
  }, []);

  React.useEffect(() => { loadList(); }, [loadList]);

  const flashCopied = (key) => {
    setCopied(key);
    setTimeout(() => setCopied(c => (c === key ? null : c)), 2600);
  };

  // 업로드 후 응답의 경로를 자동 복사하고 목록을 재동기화.
  const afterUpload = async (res) => {
    if (!res || !res.success) {
      setError(res?.error || "전송에 실패했습니다.");
      return;
    }
    setError("");
    loadList();
    const ok = await copyText(res.filepath);
    if (ok) flashCopied(res.filepath);
    else setError(`전송은 됐지만 클립보드 복사에 실패했습니다. 경로: ${res.filepath}`);
  };

  const uploadFile = async (file) => {
    if (!file || busy) return;
    setBusy(true); setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      afterUpload(await requestWithCsrf("/dev/upload/file", { body: fd }));
    } catch (e) {
      setError(e.message || "전송 실패");
    } finally { setBusy(false); }
  };

  const uploadClipboardImage = async (dataUrl) => {
    if (!dataUrl || busy) return;
    setBusy(true); setError("");
    try {
      afterUpload(await requestWithCsrf("/dev/upload/clipboard", { jsonBody: { image_data: dataUrl } }));
    } catch (e) {
      setError(e.message || "전송 실패");
    } finally { setBusy(false); }
  };

  const removeFile = async (f) => {
    if (!window.confirm(`${f.filename}\n서버에서 삭제할까요? 되돌릴 수 없습니다.`)) return;
    setBusy(true); setError("");
    try {
      const d = await requestWithCsrf(`/dev/upload/file?filename=${encodeURIComponent(f.filename)}`,
                                      { method: "DELETE" });
      if (!d.success) { setError(d.error || "삭제에 실패했습니다."); return; }
      setPicked(p => { const n = new Set(p); n.delete(f.filepath); return n; });
      loadList();
    } catch (e) {
      setError(e.message || "삭제 실패");
    } finally { setBusy(false); }
  };

  const onPick = (e) => {
    const list = e.target.files;
    if (list && list.length) Array.from(list).forEach(f => uploadFile(f));
    e.target.value = ""; // 같은 파일 재선택 허용
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer?.files;
    if (dropped && dropped.length) Array.from(dropped).forEach(f => uploadFile(f));
  };

  // 붙여넣기: 이미지면 clipboard 엔드포인트, 그 외 파일은 file 엔드포인트.
  const onPaste = (e) => {
    const items = e.clipboardData?.items || [];
    for (const it of items) {
      if (it.type && it.type.startsWith("image/")) {
        const blob = it.getAsFile();
        if (!blob) continue;
        const reader = new FileReader();
        reader.onload = (ev) => uploadClipboardImage(ev.target.result);
        reader.readAsDataURL(blob);
        e.preventDefault();
        return;
      }
      if (it.kind === "file") {
        const f = it.getAsFile();
        if (f) { uploadFile(f); e.preventDefault(); return; }
      }
    }
  };

  const copyPath = async (path) => {
    const ok = await copyText(path);
    if (ok) flashCopied(path);
    else setError(`클립보드 복사에 실패했습니다. 경로를 직접 복사해주세요: ${path}`);
  };

  // 선택한 파일들의 경로를 줄바꿈으로 이어 한 번에 복사(여러 파일을 AI에게 함께 전달).
  const copyPicked = async () => {
    const paths = files.filter(f => picked.has(f.filepath)).map(f => f.filepath);
    if (paths.length === 0) return;
    const ok = await copyText(paths.join("\n"));
    if (ok) flashCopied("__picked__");
    else setError("클립보드 복사에 실패했습니다.");
  };

  const removePicked = async () => {
    const targets = files.filter(f => picked.has(f.filepath));
    if (targets.length === 0) return;
    if (!window.confirm(`선택한 ${targets.length}개 파일을 서버에서 삭제할까요? 되돌릴 수 없습니다.`)) return;
    setBusy(true); setError("");
    try {
      for (const f of targets) {
        const d = await requestWithCsrf(`/dev/upload/file?filename=${encodeURIComponent(f.filename)}`,
                                        { method: "DELETE" });
        if (!d.success) { setError(d.error || "일부 파일을 삭제하지 못했습니다."); break; }
      }
      setPicked(new Set());
      loadList();
    } catch (e) {
      setError(e.message || "삭제 실패");
    } finally { setBusy(false); }
  };

  const togglePick = (path) => setPicked(p => {
    const n = new Set(p);
    n.has(path) ? n.delete(path) : n.add(path);
    return n;
  });

  const shown = React.useMemo(() => {
    const needle = q.trim().toLowerCase();
    return needle ? files.filter(f => f.filename.toLowerCase().includes(needle)) : files;
  }, [files, q]);
  const totalBytes = React.useMemo(() => files.reduce((s, f) => s + (f.size || 0), 0), [files]);
  const allShownPicked = shown.length > 0 && shown.every(f => picked.has(f.filepath));

  return (
    <div className="app" style={{height:'100vh'}}>
      <div className="shell">
        <Sidebar role="platform" active="uploads"/>
        <div style={{display:'flex', flexDirection:'column', overflow:'hidden'}}>
          <Topbar title="파일전송" sub="서버로 파일·이미지 전송 후 경로 복사"/>
          <div className="main">

            <div style={{display:'flex', alignItems:'flex-end', justifyContent:'space-between', marginBottom:18}}>
              <div>
                <div className="h1">파일전송</div>
                <div className="muted" style={{marginTop:4, fontSize:13}}>
                  파일이나 이미지를 서버로 전송하면 절대 경로가 자동 복사됩니다. 이 경로를 AI에게 붙여넣으면 해당 파일을 열어볼 수 있습니다.
                </div>
              </div>
              <button className="btn btn-ghost btn-md" onClick={loadList} title="목록 새로고침">새로고침</button>
            </div>

            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, alignItems:'start'}}>

              {/* 업로드 영역 */}
              <div className="card" style={{padding:22}}>
                <div className="h3">전송</div>
                <div className="muted" style={{fontSize:12, marginTop:2}}>
                  아래 영역을 클릭해 파일을 고르거나, 끌어다 놓거나, 이미지를 <b>Ctrl+V</b>로 붙여넣으세요.
                </div>

                <div
                  tabIndex={0}
                  onClick={() => inputRef.current?.click()}
                  onPaste={onPaste}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={onDrop}
                  style={{
                    marginTop:16, borderRadius:14, padding:'42px 18px', textAlign:'center',
                    border:`2px dashed ${dragOver ? 'var(--orange-500)' : 'var(--border)'}`,
                    background: dragOver ? 'var(--orange-50)' : 'var(--bg-sub)',
                    cursor:'pointer', transition:'all .15s', outline:'none',
                  }}>
                  <div style={{width:44, height:44, margin:'0 auto 12px', borderRadius:12, background:'var(--surface-1)',
                               display:'grid', placeItems:'center', color:'var(--orange-600)'}}>
                    <span style={{width:22, height:22, display:'inline-block'}}>{Ic.upload}</span>
                  </div>
                  <div style={{fontWeight:700, fontSize:14, color:'var(--ink-1)'}}>
                    {busy ? '전송 중…' : '클릭 · 드래그 · 붙여넣기'}
                  </div>
                  <div className="muted" style={{fontSize:12, marginTop:4}}>
                    모든 형식 지원 · 파일당 최대 50MB
                  </div>
                  <input ref={inputRef} type="file" multiple onChange={onPick} style={{display:'none'}}/>
                </div>

                {error && (
                  <div style={{marginTop:14, padding:'10px 12px', borderRadius:10, fontSize:12.5, lineHeight:1.6,
                               background:'var(--rose-50, #fef2f2)', color:'var(--rose-600, #e11d48)', wordBreak:'break-all'}}>
                    {error}
                  </div>
                )}
                {copied && (
                  <div style={{marginTop:14, display:'flex', alignItems:'center', gap:8, padding:'10px 12px',
                               borderRadius:10, fontSize:12.5, background:'var(--green-50, #ecfdf5)', color:'var(--green-700, #047857)'}}>
                    <span style={{width:15, height:15, display:'inline-block'}}>{Ic.check}</span>
                    {copied === "__picked__" ? '선택한 파일 경로를 모두 복사했습니다.' : '경로가 클립보드에 복사되었습니다 — AI에게 붙여넣으세요.'}
                  </div>
                )}
              </div>

              {/* 파일 목록 */}
              <div className="card" style={{padding:22}}>
                <div style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
                  <div className="h3">전송된 파일</div>
                  <span className="muted" style={{fontSize:12}}>{files.length}개 · {fmtSize(totalBytes)}</span>
                </div>
                <div className="muted" style={{fontSize:12, marginTop:2}}>최근 전송 순. 경로를 클릭해 다시 복사할 수 있습니다.</div>

                <div style={{display:'flex', gap:8, alignItems:'center', marginTop:12}}>
                  <div className="searchbar" style={{flex:1}}>
                    {Ic.search}<input className="input" placeholder="파일명 검색" value={q} onChange={e=>setQ(e.target.value)}/>
                  </div>
                  {shown.length > 0 && (
                    <button className="btn btn-ghost btn-sm" onClick={() => setPicked(p => {
                      if (allShownPicked) { const n = new Set(p); shown.forEach(f => n.delete(f.filepath)); return n; }
                      const n = new Set(p); shown.forEach(f => n.add(f.filepath)); return n;
                    })}>{allShownPicked ? '선택 해제' : '전체 선택'}</button>
                  )}
                </div>

                {picked.size > 0 && (
                  <div style={{marginTop:10, display:'flex', alignItems:'center', gap:8, padding:'8px 10px',
                               borderRadius:10, background:'var(--bg-sub)', fontSize:12}}>
                    <b>{picked.size}개 선택</b>
                    <div style={{flex:1}}/>
                    <button className="btn btn-primary btn-sm" onClick={copyPicked}>경로 일괄 복사</button>
                    <button className="btn btn-ghost btn-sm" disabled={busy} onClick={removePicked}
                            style={{color:'var(--rose-600, #e11d48)'}}>삭제</button>
                  </div>
                )}

                <div style={{marginTop:14, display:'flex', flexDirection:'column', gap:10, maxHeight:520, overflowY:'auto'}}>
                  {files.length === 0 && (
                    <div className="muted" style={{fontSize:12.5, padding:'28px 0', textAlign:'center'}}>아직 전송된 파일이 없습니다.</div>
                  )}
                  {files.length > 0 && shown.length === 0 && (
                    <div className="muted" style={{fontSize:12.5, padding:'28px 0', textAlign:'center'}}>검색 결과가 없습니다.</div>
                  )}
                  {shown.map((f) => {
                    const isImg = IMG_RE.test(f.filename);
                    const isCopied = copied === f.filepath;
                    const isPicked = picked.has(f.filepath);
                    return (
                      <div key={f.filepath} className="card card-pad"
                           style={{padding:12, display:'flex', gap:10, alignItems:'center',
                                   border: isPicked ? '1px solid var(--orange-500)' : undefined}}>
                        <input type="checkbox" checked={isPicked} onChange={() => togglePick(f.filepath)}
                               title="여러 파일 경로를 한 번에 복사" style={{flexShrink:0}}/>
                        <a href={f.url} target="_blank" rel="noreferrer" title="새 탭에서 열기"
                           style={{width:46, height:46, borderRadius:10, flexShrink:0, overflow:'hidden',
                                   background:'var(--bg-sub)', display:'grid', placeItems:'center', color:'var(--ink-3)'}}>
                          {isImg
                            ? <img src={f.url} alt="" style={{width:'100%', height:'100%', objectFit:'cover'}}/>
                            : <span style={{width:20, height:20, display:'inline-block'}}>{Ic.case}</span>}
                        </a>
                        <div style={{flex:1, minWidth:0}}>
                          <div style={{fontWeight:700, fontSize:12.5, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}
                               title={f.filename}>{f.filename}</div>
                          <div className="mono" onClick={() => copyPath(f.filepath)} title="클릭해서 경로 복사"
                               style={{fontSize:11, color:'var(--ink-3)', cursor:'pointer', overflow:'hidden',
                                       textOverflow:'ellipsis', whiteSpace:'nowrap', marginTop:2}}>
                            {f.filepath}
                          </div>
                          <div className="muted" style={{fontSize:10.5, marginTop:2}}>{fmtSize(f.size)}</div>
                        </div>
                        <button className={"btn btn-sm " + (isCopied ? "btn-primary" : "btn-ghost")}
                                onClick={() => copyPath(f.filepath)}
                                style={{flexShrink:0, display:'inline-flex', alignItems:'center', gap:5}}
                                title="절대 경로 복사">
                          <span style={{width:13, height:13, display:'inline-block'}}>{isCopied ? Ic.check : Ic.copy}</span>
                          {isCopied ? '복사됨' : '경로'}
                        </button>
                        <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => removeFile(f)}
                                title="서버에서 삭제" style={{flexShrink:0, padding:'0 7px', color:'var(--ink-3)'}}>
                          <span style={{width:13, height:13, display:'inline-block'}}>{Ic.close}</span>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>

            </div>

          </div>
          <AppFooter/>
        </div>
      </div>
    </div>
  );
}

export { FileTransfer };
